import * as React from "react";
import { cn } from "@/lib/utils";

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  variant?: "default" | "error" | "complete" | "border-none";
  size?: "default" | "lg";
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  clearable?: boolean;
  onClear?: () => void;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = "text",
      variant = "default",
      size = "default",
      leftIcon,
      rightIcon,
      clearable = false,
      onClear,
      ...props
    },
    ref
  ) => {
    const [showClear, setShowClear] = React.useState(false);
    const [showPassword, setShowPassword] = React.useState(false);

    const inputRef = React.useRef<HTMLInputElement>(null);
    const combinedRef = React.useMemo(
      () => (ref || inputRef) as React.RefObject<HTMLInputElement>,
      [ref, inputRef]
    );

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setShowClear(clearable && e.target.value.length > 0);
      props.onChange?.(e);
    };

    const handleClear = () => {
      if (combinedRef.current) {
        combinedRef.current.value = "";
        setShowClear(false);
        onClear?.();
      }
    };

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    const getInputType = () => {
      if (type === "password") {
        return showPassword ? "text" : "password";
      }
      return type;
    };

    const getVariantClasses = () => {
      switch (variant) {
        case "error":
          return "wl-input-error";
        case "complete":
          return "wl-input-complete";
        case "border-none":
          return "wl-input-border-none";
        default:
          return "";
      }
    };

    const getSizeClasses = () => {
      switch (size) {
        case "lg":
          return "wl-input-lg";
        default:
          return "";
      }
    };

    const getTypeClasses = () => {
      return `wl-input-${type}`;
    };

    const hasLeftIcon = !!leftIcon;
    const hasRightIcon = !!rightIcon || clearable || type === "password";
    const shouldShowClear = clearable && showClear && !props.disabled;
    const shouldShowPasswordToggle = type === "password" && !props.disabled;

    return (
      <div className={cn("wl-input-container", className)}>
        <input
          ref={combinedRef}
          type={getInputType()}
          data-slot="input"
          className={cn(
            getTypeClasses(),
            getVariantClasses(),
            getSizeClasses(),
            hasLeftIcon && "wl-input-has-left-icon",
            hasRightIcon && "wl-input-has-right-icon"
          )}
          onChange={handleInputChange}
          {...props}
        />

        {leftIcon && (
          <div className="wl-input-icon wl-input-icon-lead">{leftIcon}</div>
        )}

        {rightIcon && (
          <div className="wl-input-icon wl-input-icon-trail">{rightIcon}</div>
        )}

        {shouldShowClear && (
          <button
            type="button"
            className="wl-input-icon wl-input-icon-trail wl-icon-clear-circle"
            onClick={handleClear}
            aria-label="Clear input"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 15.126C4.10352 15.126 0.938477 11.9609 0.938477 8.06445C0.938477 4.16797 4.10352 1.00293 8 1.00293C11.8965 1.00293 15.0615 4.16797 15.0615 8.06445C15.0615 11.9609 11.8965 15.126 8 15.126ZM5.75781 10.9629C5.94238 10.9629 6.09961 10.8945 6.22949 10.7715L8 8.9873L9.78418 10.7715C9.90039 10.8945 10.0645 10.9629 10.249 10.9629C10.6113 10.9629 10.8984 10.6758 10.8984 10.3066C10.8984 10.1357 10.8301 9.97851 10.707 9.85547L8.92285 8.07129L10.7139 6.28027C10.8437 6.15039 10.8984 6 10.8984 5.8291C10.8984 5.4668 10.6182 5.18652 10.2627 5.18652C10.0781 5.18652 9.93457 5.24805 9.80469 5.37793L8 7.16894L6.20898 5.38477C6.08594 5.26172 5.94238 5.20019 5.75781 5.20019C5.39551 5.20019 5.11523 5.47363 5.11523 5.83594C5.11523 6.00684 5.18359 6.16406 5.30664 6.28711L7.09082 8.07129L5.30664 9.8623C5.18359 9.98535 5.11523 10.1426 5.11523 10.3066C5.11523 10.6758 5.39551 10.9629 5.75781 10.9629Z"
                fill="currentColor"
              />
            </svg>
          </button>
        )}

        {shouldShowPasswordToggle && (
          <button
            type="button"
            className={cn(
              "wl-input-icon wl-input-icon-trail",
              showPassword ? "wl-icon-eye-hide" : "wl-icon-eye"
            )}
            onClick={togglePasswordVisibility}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12.2485 12.8701L3.14453 3.7768C2.9834 3.61567 2.9834 3.33637 3.14453 3.17524C3.31641 3.00336 3.59033 3.00874 3.75684 3.17524L12.8501 12.2631C13.0166 12.4296 13.0273 12.6874 12.8501 12.8701C12.6782 13.0473 12.415 13.0366 12.2485 12.8701ZM8 3.51899C12.3506 3.51899 15.3047 7.03168 15.3047 8.12202C15.3047 8.73432 14.3594 10.1254 12.7588 11.2104L10.7285 9.18549C10.8682 8.86323 10.9487 8.49799 10.9487 8.12202C10.9487 6.49458 9.62744 5.17329 8 5.17329C7.62402 5.17329 7.25879 5.25385 6.93115 5.3935L5.48096 3.93794C6.24902 3.68012 7.09766 3.51899 8 3.51899ZM8 12.7197C3.70312 12.7197 0.695312 9.20698 0.695312 8.12202C0.695312 7.48823 1.71045 6.0434 3.39697 4.94233L5.33594 6.87592C5.15332 7.25727 5.05127 7.67622 5.05127 8.12202C5.05127 9.74946 6.37256 11.0707 8 11.0707C8.4458 11.0707 8.87012 10.9687 9.24609 10.7807L10.7178 12.247C9.896 12.5371 8.98291 12.7197 8 12.7197ZM9.76709 8.22407L7.89795 6.35493C8.97754 6.25825 9.85303 7.24116 9.76709 8.22407ZM8 9.89985C7.01709 9.89985 6.22217 9.10493 6.22217 8.12202C6.22217 8.00922 6.23828 7.9018 6.25977 7.79438L8.32764 9.86225C8.22021 9.88374 8.11279 9.89985 8 9.89985Z"
                  fill="currentColor"
                />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M7.99993 13.1835C8.72592 13.1835 9.40864 13.097 10.0481 12.9239C10.6876 12.7509 11.2763 12.5233 11.8142 12.2412C12.3558 11.9553 12.8392 11.6412 13.2643 11.2989C13.6893 10.9566 14.0504 10.6124 14.3476 10.2664C14.6448 9.92032 14.8723 9.60059 15.0303 9.30718C15.1883 9.01378 15.2673 8.7768 15.2673 8.59625C15.2673 8.41569 15.1883 8.18059 15.0303 7.89095C14.8723 7.59755 14.6448 7.27781 14.3476 6.93175C14.0504 6.58568 13.6874 6.2415 13.2586 5.8992C12.8336 5.55689 12.3502 5.24468 11.8085 4.96256C11.2706 4.67669 10.6819 4.44723 10.0425 4.2742C9.40676 4.10116 8.72592 4.01465 7.99993 4.01465C7.28523 4.01465 6.60815 4.10116 5.96868 4.2742C5.33298 4.44723 4.74429 4.67669 4.20262 4.96256C3.66472 5.24468 3.18136 5.55689 2.75254 5.8992C2.32748 6.2415 1.96261 6.58568 1.65792 6.93175C1.35699 7.27781 1.12566 7.59755 0.963908 7.89095C0.805922 8.18059 0.726929 8.41569 0.726929 8.59625C0.726929 8.7768 0.805922 9.01378 0.963908 9.30718C1.12566 9.60059 1.35699 9.92032 1.65792 10.2664C1.96261 10.6124 2.32748 10.9566 2.75254 11.2989C3.18136 11.6412 3.66472 11.9553 4.20262 12.2412C4.74429 12.5233 5.33298 12.7509 5.96868 12.9239C6.60815 13.097 7.28523 13.1835 7.99993 13.1835ZM7.99993 11.4908C7.46203 11.4908 6.97302 11.3591 6.53292 11.0958C6.09658 10.8325 5.74675 10.4827 5.48344 10.0463C5.22389 9.60623 5.09224 9.12475 5.08847 8.60189C5.08847 8.06022 5.21825 7.57122 5.4778 7.13487C5.74111 6.69853 6.09281 6.35247 6.53292 6.09668C6.97302 5.83713 7.46203 5.70736 7.99993 5.70736C8.53408 5.70736 9.02120 5.83713 9.46130 6.09668C9.90517 6.35247 10.2569 6.70041 10.5164 7.14052C10.7797 7.57686 10.9114 8.06398 10.9114 8.60189C10.9114 8.99309 10.8362 9.36361 10.6857 9.71343C10.5352 10.0595 10.3265 10.3679 10.0594 10.6388C9.79232 10.9059 9.48199 11.1146 9.12840 11.2651C8.77858 11.4155 8.40242 11.4908 7.99993 11.4908ZM7.99993 9.69651C8.30086 9.69651 8.55853 9.58930 8.77294 9.37489C8.99111 9.15672 9.10019 8.89905 9.10019 8.60189C9.10019 8.30096 8.99111 8.04329 8.77294 7.82888C8.55853 7.61447 8.30086 7.50727 7.99993 7.50727C7.69901 7.50727 7.43946 7.61447 7.22129 7.82888C7.00688 8.04329 6.89967 8.30096 6.89967 8.60189C6.89967 8.89905 7.00688 9.15672 7.22129 9.37489C7.43946 9.58930 7.69901 9.69651 7.99993 9.69651Z"
                  fill="currentColor"
                />
              </svg>
            )}
          </button>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export { Input };
